import { AdType } from '@/shared/constants'
import { InjectionKey, Ref } from 'vue'

export enum BannerPosition {
  Header = 'header',
  InVideo = 'in-video',
  NearVideo = 'near-video',
  AfterVideo = 'after-video',
  Footer = 'footer',
  Within = 'within'
}

export enum BannerType {
  Regular = 'regular',
  Native = 'native'
}

export enum TransactionType {
  Added = 0,
  Withdrawn = 1,
  Spends = 2,
  Refund = 3,
  SystemFee = 4,
  Replenishment = 6
}

export enum VerificationType {
  Profile = 'contact',
  Identity = 'identity',
  Agreement = 'agreement',
  Country = 'country',
  ContactType = 'contact_type',
  User = 'user_id',
  PaymentProfile = 'payment_profile'
}

export enum AdScanFrequency {
  Disabled = 'disabled',
  Hourly = 'hourly',
  Daily = 'daily',
  Weekly = 'weekly'
}

export enum Stream {
  Adult = 1,
  Gay = 2,
  Toons = 3,
  Trans = 4,
  Mainstream = 5,
  Fallback = 99
}

export enum IspMode {
  Except = 'except',
  Only = 'only',
  Any = 'any'
}

// Streams that can be selected by a user
export type SelectableStream =
  | Stream.Adult
  | Stream.Gay
  | Stream.Toons
  | Stream.Trans
  | Stream.Mainstream
// Human-readable stream names
export type StreamLabel = {
  stream: SelectableStream
  label: string
  icon?: string
}

export const StreamLabels: Array<StreamLabel> = [
  {
    stream: Stream.Adult,
    label: 'Adult-all'
  },
  {
    stream: Stream.Mainstream,
    label: 'Mainstream (non-adult)'
  },
  {
    stream: Stream.Gay,
    label: 'Adult-gay'
  },
  {
    stream: Stream.Toons,
    label: 'Adult-toons'
  },
  {
    stream: Stream.Trans,
    label: 'Adult-trans'
  }
]

// One definition, in the layer both apps can reach. This file kept a byte-identical
// copy and `src/apps/adok/api/types.ts` a third that had already lost `Tab = 12` —
// three places to add a format to, and nothing that noticed when one was missed.
// Legacy may import `@/shared`; the reverse is what `check:fsd` forbids.
//
// Imported as well as re-exported: the unions below name `AdType.Popunder` and friends,
// and a bare `export … from` does not bring the name into this file's own scope.
export { AdType }

// Ad types available to adverts
export type DspAdType =
  | AdType.Popunder
  | AdType.Interstitial
  | AdType.Banner
  | AdType.Overlay
  | AdType.InpagePush
  | AdType.Slider
  | AdType.OutStream
export type SspAdType =
  | AdType.Popunder
  | AdType.Interstitial
  | AdType.Banner
  | AdType.Overlay
  | AdType.InpagePush
  | AdType.Slider
  | AdType.OutStream
  | AdType.Im
  | AdType.Skim

// Human-readable ad type names
export type AdTypeLabel = {
  id?: AdType
  type_id?: AdType
  label: string
  icon?: string
  image?: string
  mobileImage?: string
}

export const DspAdTypeLabels: Array<AdTypeLabel> = [
  {
    type_id: AdType.Popunder,
    label: 'Popunder'
  },
  {
    type_id: AdType.Interstitial,
    label: 'Interstitial'
  },
  {
    type_id: AdType.Banner,
    label: 'Banner'
  },
  {
    type_id: AdType.Overlay,
    label: 'In-video (VAST Link URL)'
  },
  {
    type_id: AdType.Slider,
    label: 'Slider (VAST Link URL)'
  },
  {
    type_id: AdType.OutStream,
    label: 'Outstream (VAST Link URL)'
  },
  {
    type_id: AdType.InpagePush,
    label: 'In-Page Push (Link URL)'
  }
]

export const SspAdTypeLabels: Array<AdTypeLabel> = [
  {
    type_id: AdType.Popunder,
    label: 'Popunder'
  },
  {
    type_id: AdType.Banner,
    label: 'Banner'
  },
  {
    type_id: AdType.Overlay,
    label: 'In-video'
  },
  {
    type_id: AdType.Slider,
    label: 'Slider'
  },
  {
    type_id: AdType.InpagePush,
    label: 'In-Page Push'
  },
  {
    type_id: AdType.Interstitial,
    label: 'Interstitial'
  },
  {
    type_id: AdType.OutStream,
    label: 'OutStream'
  }
]

// Sources quality
export enum QualityType {
  All,
  Low,
  Normal,
  High
}

// Human-readable quality types
export type QualityTypeLabel = {
  type_id: QualityType
  label: string
}

export const QualityTypeLabels: Array<QualityTypeLabel> = [
  {
    type_id: QualityType.All,
    label: 'All'
  },
  {
    type_id: QualityType.Low,
    label: 'Low'
  },
  {
    type_id: QualityType.Normal,
    label: 'Normal'
  },
  {
    type_id: QualityType.High,
    label: 'High'
  }
]

// Protocols available to feed format
export enum ProtocolType {
  Xml = 'xml',
  Json = 'json'
}
// Human-readable protocol types
export type ProtocolTypeLabel = {
  type_id: ProtocolType
  label: string
}

export const ProtocolTypeLabels: Array<ProtocolTypeLabel> = [
  {
    type_id: ProtocolType.Xml,
    label: 'XML'
  },
  {
    type_id: ProtocolType.Json,
    label: 'JSON'
  }
]

export type ProtocolTypeFilterLabel = {
  type_id: number
  label: string
}

export const ProtocolTypeFilterLabels: Array<ProtocolTypeFilterLabel> = [
  {
    type_id: 1,
    label: 'XML'
  },
  {
    type_id: 2,
    label: 'JSON'
  },
  {
    type_id: 0,
    label: 'oRTB'
  }
]

export type ProtocolLabel = {
  type_id: string
  label: string
}

export const ProtocolLabels: Array<ProtocolLabel> = [
  {
    type_id: 'xml',
    label: 'XML'
  },
  {
    type_id: 'json',
    label: 'JSON'
  },
  {
    type_id: 'rtb',
    label: 'oRTB'
  }
]

// Pricing available to feed format
export enum PricingType {
  Cpm = 1,
  Cpv = 2
}
// Human-readable protocol types
export type PricingTypeLabel = {
  type_id: PricingType
  label: string
}

export const PricingTypeLabels: Array<PricingTypeLabel> = [
  {
    type_id: PricingType.Cpm,
    label: 'CPM'
  },
  {
    type_id: PricingType.Cpv,
    label: 'CPV'
  }
]

export enum VideoSource {
  Tag = 1,
  File = 2,
  Url = 3
}

// Types that can be selected by a user
// Human-readable type names
export type VideoSourceLabel = {
  type: VideoSource
  label: string
}

export const VideoSourceLabels: Array<VideoSourceLabel> = [
  {
    type: VideoSource.File,
    label: 'Video file'
  },
  {
    type: VideoSource.Url,
    label: 'Video URL'
  },
  {
    type: VideoSource.Tag,
    label: 'VAST tag'
  }
]

export enum HistoryItem {
  Campaign = 'Campaign',
  Creative = 'Creature',
  Feed = 'Feed',
  FeedFormat = 'FeedFormat'
}

// Types that can be selected by a user
// Human-readable type names
export type HistoryItemLabel = {
  type: HistoryItem
  label: string
}

export const HistoryItemLabels: Array<HistoryItemLabel> = [
  {
    type: HistoryItem.Campaign,
    label: 'Campaign'
  },
  {
    type: HistoryItem.Creative,
    label: 'Creative'
  },
  {
    type: HistoryItem.Feed,
    label: 'Feed'
  },
  {
    type: HistoryItem.FeedFormat,
    label: 'FeedFormat'
  }
]

// RonType
export enum RonType {
  All,
  Only,
  Except
}

export type RonTypeLabel = {
  type_id: RonType
  label: string
  tooltip?: string
}

export const RonTypeLabels: Array<RonTypeLabel> = [
  {
    type_id: RonType.All,
    label: 'All spots',
    tooltip:
      'Your campaign receives traffic from all available spots. Domain, IP, and ISP filters still apply.'
  },
  {
    type_id: RonType.Only,
    label: 'Only selected spots',
    tooltip:
      'Your campaign receives traffic only from spots you add below — manually or from a saved list.'
  },
  {
    type_id: RonType.Except,
    label: 'Exclude selected spots',
    tooltip:
      'Your campaign receives traffic from all spots except the ones you add below.'
  }
]

// Device
export enum Device {
  Desktop = 1,
  Mobile = 2,
  Tablet = 3,
  Console = 4,
  TV = 5,
  Wearable = 6
}

export type DeviceTypeLabel = {
  type_id: Device
  label: string
}

export const DeviceTypeLabels: Array<DeviceTypeLabel> = [
  {
    type_id: Device.Desktop,
    label: 'Desktop'
  },
  {
    type_id: Device.Mobile,
    label: 'Mobile'
  },
  {
    type_id: Device.Tablet,
    label: 'Tablet'
  },
  {
    type_id: Device.Console,
    label: 'Console'
  },
  {
    type_id: Device.TV,
    label: 'TV'
  },
  {
    type_id: Device.Wearable,
    label: 'Wearable'
  }
]

// FlatDeal
export enum FlatDealType {
  CpmRate,
  DailyFee
}

export type FlatDealTypeLabel = {
  type_id: FlatDealType
  label: string
}

export const FlatDealTypeLabels: Array<FlatDealTypeLabel> = [
  {
    type_id: FlatDealType.CpmRate,
    label: 'CPM rate'
  },
  {
    type_id: FlatDealType.DailyFee,
    label: 'Daily fee'
  }
]

export enum Size {
  Size300250 = 14,
  Size300100 = 27,
  Size72890 = 19,
  Size46860 = 18,
  Size12090 = 2,
  Size250250 = 13,
  Size900250 = 31,
  SizeAdaptive = 33
}

export type SizeLabel = {
  id: Size
  label: string
}

export const SizeLabels: Array<SizeLabel> = [
  {
    id: Size.Size300250,
    label: '300x250'
  },
  {
    id: Size.Size300100,
    label: '300x100'
  },
  {
    id: Size.Size72890,
    label: '728x90'
  },
  {
    id: Size.Size46860,
    label: '468x60'
  },
  {
    id: Size.Size12090,
    label: '120x90'
  },
  {
    id: Size.Size250250,
    label: '250x250'
  },
  {
    id: Size.Size900250,
    label: '900x250'
  }
]

export const AdaptiveBannerSize: SizeLabel = {
  id: Size.SizeAdaptive,
  label: 'Adaptive'
}

export enum ConnectionType {
  Dialup = 1,
  Cable = 2,
  Corporate = 3,
  Cellular = 4,
  Other = 5
}

export type ConnectionTypeLabel = {
  type_id: ConnectionType
  label: string
}

export const ConnectionTypeLabels: Array<ConnectionTypeLabel> = [
  {
    type_id: ConnectionType.Dialup,
    label: 'Dialup'
  },
  {
    type_id: ConnectionType.Cable,
    label: 'Cable'
  },
  {
    type_id: ConnectionType.Corporate,
    label: 'Corporate'
  },
  {
    type_id: ConnectionType.Cellular,
    label: 'Cellular'
  },
  {
    type_id: ConnectionType.Other,
    label: 'Other'
  }
]

export enum Platform {
  Windows = '1',
  Mac = '2',
  Linux = '3',
  iOS = '5',
  Android = '6',
  ChromeOS = '7',
  WinPhone = '8',
  Blackberry = '9',
  WebOS = '10',
  Playstation = '11',
  Xbox = '12',
  Nintendo = '13',
  Kindle = '14',
  Other = '4'
}

export type PlatformKey = keyof typeof Platform

export type PlatformLabel = {
  type_id: Platform
  label: string
}

export const PlatformLabels: Array<PlatformLabel> = [
  {
    type_id: Platform.Windows,
    label: 'Windows'
  },
  {
    type_id: Platform.Mac,
    label: 'Mac'
  },
  {
    type_id: Platform.Linux,
    label: 'Linux'
  },
  {
    type_id: Platform.Other,
    label: 'Other'
  },
  {
    type_id: Platform.iOS,
    label: 'iOS'
  },
  {
    type_id: Platform.Android,
    label: 'Android'
  },
  {
    type_id: Platform.ChromeOS,
    label: 'ChromeOS'
  },
  {
    type_id: Platform.WinPhone,
    label: 'Windows Phone'
  },
  {
    type_id: Platform.Blackberry,
    label: 'Blackberry'
  },
  {
    type_id: Platform.WebOS,
    label: 'Web OS'
  },
  {
    type_id: Platform.Playstation,
    label: 'Sony Playstation'
  },
  {
    type_id: Platform.Xbox,
    label: 'Microsoft Xbox'
  },
  {
    type_id: Platform.Nintendo,
    label: 'Nintendo'
  },
  {
    type_id: Platform.Kindle,
    label: 'Amazon Kindle'
  }
]

export enum Browser {
  InternetExplorer = '1',
  Firefox = '2',
  Chrome = '3',
  Opera = '4',
  Safari = '5',
  Android = '7',
  UCBrowser = '8',
  Blackberry = '9',
  Silk = '10',
  TencentQQ = '11',
  Spotify = '12',
  Yandex = '13',
  Nintendo = '14',
  Samsung = '15',
  CocCoc = '16',
  Baidu = '17',
  GoogleSearchApp = '18',
  Aloha = '19',
  Other = '6'
}

export type BrowserKey = keyof typeof Browser

export type BrowserLabel = {
  type_id: Browser
  label: string
}

export const BrowserLabels: Array<BrowserLabel> = [
  {
    type_id: Browser.InternetExplorer,
    label: 'Microsoft Edge'
  },
  {
    type_id: Browser.Firefox,
    label: 'Firefox'
  },
  {
    type_id: Browser.Chrome,
    label: 'Chrome'
  },
  {
    type_id: Browser.Opera,
    label: 'Opera'
  },
  {
    type_id: Browser.Safari,
    label: 'Safari'
  },
  {
    type_id: Browser.Other,
    label: 'Other'
  },
  {
    type_id: Browser.Android,
    label: 'Android WebView'
  },
  {
    type_id: Browser.UCBrowser,
    label: 'UC Browser'
  },
  {
    type_id: Browser.Blackberry,
    label: 'BlackBerry'
  },
  {
    type_id: Browser.Silk,
    label: 'Amazon Silk'
  },
  {
    type_id: Browser.TencentQQ,
    label: 'Tencent QQ'
  },
  {
    type_id: Browser.Spotify,
    label: 'Spotify'
  },
  {
    type_id: Browser.Yandex,
    label: 'Yandex'
  },
  {
    type_id: Browser.Nintendo,
    label: 'Nintendo Browser'
  },
  {
    type_id: Browser.Samsung,
    label: 'Samsung Internet'
  },
  {
    type_id: Browser.CocCoc,
    label: 'Coc Coc'
  },
  {
    type_id: Browser.Baidu,
    label: 'Baidu App Box'
  },
  {
    type_id: Browser.GoogleSearchApp,
    label: 'Google Search App'
  },
  {
    type_id: Browser.Aloha,
    label: 'Aloha Browser'
  }
]

export enum Weekday {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6
}

export type WeekdayKey = keyof typeof Weekday

export type WeekdayLabel = {
  type_id: Weekday
  label: string
}

export const WeekdayLabels: Array<WeekdayLabel> = [
  {
    type_id: Weekday.Sunday,
    label: 'Sunday'
  },
  {
    type_id: Weekday.Monday,
    label: 'Monday'
  },
  {
    type_id: Weekday.Tuesday,
    label: 'Tuesday'
  },
  {
    type_id: Weekday.Wednesday,
    label: 'Wednesday'
  },
  {
    type_id: Weekday.Thursday,
    label: 'Thursday'
  },
  {
    type_id: Weekday.Friday,
    label: 'Friday'
  },
  {
    type_id: Weekday.Saturday,
    label: 'Saturday'
  }
]

export enum Timeslot {
  Hour00,
  Hour01,
  Hour02,
  Hour03,
  Hour04,
  Hour05,
  Hour06,
  Hour07,
  Hour08,
  Hour09,
  Hour10,
  Hour11,
  Hour12,
  Hour13,
  Hour14,
  Hour15,
  Hour16,
  Hour17,
  Hour18,
  Hour19,
  Hour20,
  Hour21,
  Hour22,
  Hour23
}

export type TimeslotKey = keyof typeof Timeslot

export type TimeslotLabel = {
  type_id: Timeslot
  label: string
}

export const TimeslotLabels: Array<TimeslotLabel> = [
  {
    type_id: Timeslot.Hour00,
    label: '00:00-00:59'
  },
  {
    type_id: Timeslot.Hour01,
    label: '01:00-01:59'
  },
  {
    type_id: Timeslot.Hour02,
    label: '02:00-02:59'
  },
  {
    type_id: Timeslot.Hour03,
    label: '03:00-03:59'
  },
  {
    type_id: Timeslot.Hour04,
    label: '04:00-04:59'
  },
  {
    type_id: Timeslot.Hour05,
    label: '05:00-05:59'
  },
  {
    type_id: Timeslot.Hour06,
    label: '06:00-06:59'
  },
  {
    type_id: Timeslot.Hour07,
    label: '07:00-07:59'
  },
  {
    type_id: Timeslot.Hour08,
    label: '08:00-08:59'
  },
  {
    type_id: Timeslot.Hour09,
    label: '09:00-09:59'
  },
  {
    type_id: Timeslot.Hour10,
    label: '10:00-10:59'
  },
  {
    type_id: Timeslot.Hour11,
    label: '11:00-11:59'
  },
  {
    type_id: Timeslot.Hour12,
    label: '12:00-12:59'
  },
  {
    type_id: Timeslot.Hour13,
    label: '13:00-13:59'
  },
  {
    type_id: Timeslot.Hour14,
    label: '14:00-14:59'
  },
  {
    type_id: Timeslot.Hour15,
    label: '15:00-15:59'
  },
  {
    type_id: Timeslot.Hour16,
    label: '16:00-16:59'
  },
  {
    type_id: Timeslot.Hour17,
    label: '17:00-17:59'
  },
  {
    type_id: Timeslot.Hour18,
    label: '18:00-18:59'
  },
  {
    type_id: Timeslot.Hour19,
    label: '19:00-19:59'
  },
  {
    type_id: Timeslot.Hour20,
    label: '20:00-20:59'
  },
  {
    type_id: Timeslot.Hour21,
    label: '21:00-21:59'
  },
  {
    type_id: Timeslot.Hour22,
    label: '22:00-22:59'
  },
  {
    type_id: Timeslot.Hour23,
    label: '23:00-23:59'
  }
]

export enum DeviceLanguage {
  Afrikaans = 1,
  Amharic = 2,
  ModernStandardArabic = 3,
  Azerbaijani = 4,
  Bulgarian = 5,
  Bengali = 6,
  Catalan = 8,
  Czech = 9,
  Danish = 10,
  German = 11,
  Greek = 12,
  English = 13,
  AmericanEnglish = 14,
  BritishEnglish = 15,
  Spanish = 16,
  EuropeanSpanish = 17,
  LatinAmericanSpanish = 18,
  Estonian = 19,
  Persian = 20,
  Finnish = 21,
  Filipino = 22,
  French = 23,
  CanadianFrench = 24,
  Gujarati = 25,
  Hebrew = 26,
  Hindi = 27,
  Croatian = 28,
  Hungarian = 29,
  Armenian = 30,
  Indonesian = 31,
  Icelandic = 32,
  Italian = 33,
  Japanese = 34,
  Georgian = 35,
  Kazakh = 36,
  Khmer = 37,
  Kannada = 38,
  Korean = 39,
  Kirghiz = 40,
  Lao = 41,
  Lithuanian = 42,
  Latvian = 43,
  Macedonian = 44,
  Malayalam = 45,
  Mongolian = 46,
  Marathi = 47,
  Malay = 48,
  Burmese = 49,
  Nepali = 50,
  Dutch = 51,
  Norwegian = 52,
  Punjabi = 53,
  Polish = 54,
  Portuguese = 55,
  BrazilianPortuguese = 56,
  EuropeanPortuguese = 57,
  Romanian = 58,
  Russian = 59,
  Sinhala = 60,
  Slovak = 61,
  Slovenian = 62,
  Albanian = 63,
  Serbian = 64,
  SerbianLatin = 65,
  Swedish = 66,
  Swahili = 67,
  Tamil = 68,
  Telugu = 69,
  Thai = 70,
  Turkish = 71,
  Ukrainian = 72,
  Urdu = 73,
  Uzbek = 74,
  Vietnamese = 75,
  Chinese = 76,
  SimplifiedChinese = 77,
  TraditionalChinese = 78,
  Zulu = 79
}

export type DeviceLanguageLabel = {
  type_id: DeviceLanguage
  label: string
}

export const DeviceLanguageLabels: Array<DeviceLanguageLabel> = [
  {
    type_id: DeviceLanguage.English,
    label: 'English'
  },
  {
    type_id: DeviceLanguage.Norwegian,
    label: 'Norwegian'
  },
  {
    type_id: DeviceLanguage.German,
    label: 'German'
  },
  {
    type_id: DeviceLanguage.Italian,
    label: 'Italian'
  },
  {
    type_id: DeviceLanguage.Finnish,
    label: 'Finnish'
  },
  {
    type_id: DeviceLanguage.French,
    label: 'French'
  },
  {
    type_id: DeviceLanguage.Spanish,
    label: 'Spanish'
  },
  {
    type_id: DeviceLanguage.Czech,
    label: 'Czech'
  },
  {
    type_id: DeviceLanguage.Danish,
    label: 'Danish'
  },
  {
    type_id: DeviceLanguage.AmericanEnglish,
    label: 'American English'
  },
  {
    type_id: DeviceLanguage.BritishEnglish,
    label: 'British English'
  },
  {
    type_id: DeviceLanguage.Afrikaans,
    label: 'Afrikaans'
  },
  {
    type_id: DeviceLanguage.Amharic,
    label: 'Amharic'
  },
  {
    type_id: DeviceLanguage.ModernStandardArabic,
    label: 'Modern Standard Arabic'
  },
  {
    type_id: DeviceLanguage.Azerbaijani,
    label: 'Azerbaijani'
  },
  {
    type_id: DeviceLanguage.Bulgarian,
    label: 'Bulgarian'
  },
  {
    type_id: DeviceLanguage.Bengali,
    label: 'Bengali'
  },
  {
    type_id: DeviceLanguage.Catalan,
    label: 'Catalan'
  },
  {
    type_id: DeviceLanguage.Greek,
    label: 'Greek'
  },
  {
    type_id: DeviceLanguage.EuropeanSpanish,
    label: 'European Spanish'
  },
  {
    type_id: DeviceLanguage.LatinAmericanSpanish,
    label: 'Latin American Spanish'
  },
  {
    type_id: DeviceLanguage.Estonian,
    label: 'Estonian'
  },
  {
    type_id: DeviceLanguage.Persian,
    label: 'Persian'
  },
  {
    type_id: DeviceLanguage.Filipino,
    label: 'Filipino'
  },
  {
    type_id: DeviceLanguage.CanadianFrench,
    label: 'Canadian French'
  },
  {
    type_id: DeviceLanguage.Gujarati,
    label: 'Gujarati'
  },
  {
    type_id: DeviceLanguage.Hebrew,
    label: 'Hebrew'
  },
  {
    type_id: DeviceLanguage.Hindi,
    label: 'Hindi'
  },
  {
    type_id: DeviceLanguage.Croatian,
    label: 'Croatian'
  },
  {
    type_id: DeviceLanguage.Hungarian,
    label: 'Hungarian'
  },
  {
    type_id: DeviceLanguage.Armenian,
    label: 'Armenian'
  },
  {
    type_id: DeviceLanguage.Indonesian,
    label: 'Indonesian'
  },
  {
    type_id: DeviceLanguage.Icelandic,
    label: 'Icelandic'
  },
  {
    type_id: DeviceLanguage.Japanese,
    label: 'Japanese'
  },
  {
    type_id: DeviceLanguage.Georgian,
    label: 'Georgian'
  },
  {
    type_id: DeviceLanguage.Kazakh,
    label: 'Kazakh'
  },
  {
    type_id: DeviceLanguage.Khmer,
    label: 'Khmer'
  },
  {
    type_id: DeviceLanguage.Kannada,
    label: 'Kannada'
  },
  {
    type_id: DeviceLanguage.Korean,
    label: 'Korean'
  },
  {
    type_id: DeviceLanguage.Kirghiz,
    label: 'Kirghiz'
  },
  {
    type_id: DeviceLanguage.Lao,
    label: 'Lao'
  },
  {
    type_id: DeviceLanguage.Lithuanian,
    label: 'Lithuanian'
  },
  {
    type_id: DeviceLanguage.Latvian,
    label: 'Latvian'
  },
  {
    type_id: DeviceLanguage.Macedonian,
    label: 'Macedonian'
  },
  {
    type_id: DeviceLanguage.Malayalam,
    label: 'Malayalam'
  },
  {
    type_id: DeviceLanguage.Mongolian,
    label: 'Mongolian'
  },
  {
    type_id: DeviceLanguage.Marathi,
    label: 'Marathi'
  },
  {
    type_id: DeviceLanguage.Malay,
    label: 'Malay'
  },
  {
    type_id: DeviceLanguage.Burmese,
    label: 'Burmese'
  },
  {
    type_id: DeviceLanguage.Nepali,
    label: 'Nepali'
  },
  {
    type_id: DeviceLanguage.Dutch,
    label: 'Dutch'
  },
  {
    type_id: DeviceLanguage.Punjabi,
    label: 'Punjabi'
  },
  {
    type_id: DeviceLanguage.Polish,
    label: 'Polish'
  },
  {
    type_id: DeviceLanguage.Portuguese,
    label: 'Portuguese'
  },
  {
    type_id: DeviceLanguage.BrazilianPortuguese,
    label: 'Brazilian Portuguese'
  },
  {
    type_id: DeviceLanguage.EuropeanPortuguese,
    label: 'European Portuguese'
  },
  {
    type_id: DeviceLanguage.Romanian,
    label: 'Romanian'
  },
  {
    type_id: DeviceLanguage.Russian,
    label: 'Russian'
  },
  {
    type_id: DeviceLanguage.Sinhala,
    label: 'Sinhala'
  },
  {
    type_id: DeviceLanguage.Slovak,
    label: 'Slovak'
  },
  {
    type_id: DeviceLanguage.Slovenian,
    label: 'Slovenian'
  },
  {
    type_id: DeviceLanguage.Albanian,
    label: 'Albanian'
  },
  {
    type_id: DeviceLanguage.Serbian,
    label: 'Serbian'
  },
  {
    type_id: DeviceLanguage.SerbianLatin,
    label: 'Serbian Latin'
  },
  {
    type_id: DeviceLanguage.Swedish,
    label: 'Swedish'
  },
  {
    type_id: DeviceLanguage.Swahili,
    label: 'Swahili'
  },
  {
    type_id: DeviceLanguage.Tamil,
    label: 'Tamil'
  },
  {
    type_id: DeviceLanguage.Telugu,
    label: 'Telugu'
  },
  {
    type_id: DeviceLanguage.Thai,
    label: 'Thai'
  },
  {
    type_id: DeviceLanguage.Turkish,
    label: 'Turkish'
  },
  {
    type_id: DeviceLanguage.Ukrainian,
    label: 'Ukrainian'
  },
  {
    type_id: DeviceLanguage.Urdu,
    label: 'Urdu'
  },
  {
    type_id: DeviceLanguage.Uzbek,
    label: 'Uzbek'
  },
  {
    type_id: DeviceLanguage.Vietnamese,
    label: 'Vietnamese'
  },
  {
    type_id: DeviceLanguage.Chinese,
    label: 'Chinese'
  },
  {
    type_id: DeviceLanguage.SimplifiedChinese,
    label: 'Simplified Chinese'
  },
  {
    type_id: DeviceLanguage.TraditionalChinese,
    label: 'Traditional Chinese'
  },
  {
    type_id: DeviceLanguage.Zulu,
    label: 'Zulu'
  }
]

export enum ProxyType {
  Vpn = 1,
  Tor,
  DCH,
  PublicProxy,
  WebProxy,
  Other,
  SearchEngine
}

export type ProxyTypeKey = keyof typeof ProxyType

export type ProxyTypeLabel = {
  proxyType: ProxyType
  label: string
}

export const ProxyTypeLabels: Array<ProxyTypeLabel> = [
  {
    proxyType: ProxyType.Vpn,
    label: 'Anonymizing VPN services'
  },
  {
    proxyType: ProxyType.Tor,
    label: 'Tor Exit Nodes'
  },
  {
    proxyType: ProxyType.DCH,
    label: 'Hosting Provider, Data Center or CDN'
  },
  {
    proxyType: ProxyType.PublicProxy,
    label: 'Public Proxies'
  },
  {
    proxyType: ProxyType.WebProxy,
    label: 'Web Proxies'
  },
  {
    proxyType: ProxyType.SearchEngine,
    label: 'Search Engine Robots'
  }
]

export type CampaignClickLimitType =
  | 'clicks_hourly'
  | 'clicks_daily'
  | 'clicks_total'
  | 'clicks_spot_hourly'
  | 'clicks_domain_hourly'

export type CampaignBudgetLimitType =
  | 'budget_total'
  | 'budget_hourly'
  | 'budget_daily'

export type CampaignLimitType = CampaignClickLimitType | CampaignBudgetLimitType

export interface ILimit {
  type_id: CampaignLimitType
  value?: number
}

export type CountryISO = string

export interface ICountryCPM {
  id: CountryISO
  value: number | string
}

export interface IWinPriceItem {
  win_rate: number
  price: number
}

export interface IWinPrices {
  country: string
  data: IWinPriceItem[]
}

export type CreativeState =
  | 'new'
  | 'review'
  | 'rejected'
  | 'no_funds'
  | 'approved'
  | 'paused'
  | 'completed'
  | ''
export type CreativeStateLabel = {
  label: string
  type: string
  type_id: string
}
export const CreativeStateLabels: {
  [key in CreativeState]?: CreativeStateLabel
} = {
  new: {
    label: 'New',
    type: 'info',
    type_id: 'new'
  },
  review: {
    label: 'Under review',
    type: 'warning',
    type_id: 'review'
  },
  approved: {
    label: 'Active',
    type: 'success',
    type_id: 'approved'
  },
  paused: {
    label: 'Paused',
    type: 'warning',
    type_id: 'paused'
  },
  completed: {
    label: 'Archived',
    type: 'info',
    type_id: 'completed'
  },
  no_funds: {
    label: 'NO Funds',
    type: 'danger',
    type_id: 'no_funds'
  },
  rejected: {
    label: 'Rejected',
    type: 'danger',
    type_id: 'rejected'
  }
}

export type FeedState =
  | 'new'
  | 'review'
  | 'approved'
  | 'completed'
  | 'paused'
  | ''
export type FeedStateLabel = {
  label: string
  type: string
  type_id: string
}

export const FeedStateLabels: { [key in FeedState]?: FeedStateLabel } = {
  new: {
    label: 'New',
    type: 'info',
    type_id: 'new'
  },
  review: {
    label: 'Under review',
    type: 'warning',
    type_id: 'review'
  },
  approved: {
    label: 'Active',
    type: 'success',
    type_id: 'approved'
  },
  paused: {
    label: 'Paused',
    type: 'warning',
    type_id: 'paused'
  },
  completed: {
    label: 'Archived',
    type: 'info',
    type_id: 'completed'
  }
}

export enum FlatDealStates {
  Paused = 'paused',
  Active = 'active',
  Completed = 'completed',
  Published = 'published',
  AwaitingManagerReview = 'awaiting_manager_review',
  AwaitingUserReview = 'awaiting_user_review',
  Rejected = 'rejected'
}
export type FlatDealStateLabel = {
  label: string
  type: string
}
export const ShowFlatDealStateLabels: {
  [key in FlatDealStates]?: FlatDealStateLabel
} = {
  awaiting_manager_review: {
    label: 'Under manager review',
    type: 'warning'
  },
  awaiting_user_review: {
    label: 'Under publisher review',
    type: 'warning'
  }
}

export type SmartlinkStatus = 'direct' | 'trusted'
export type SmartlinkStatusLabel = {
  label: string
  value: string
}
export type SmartlinkStatusOption = {
  label: string
  value: SmartlinkStatus
}
export const SmartlinkStatusOptions: Array<SmartlinkStatusOption> = [
  {
    label: 'Only direct Ad',
    value: 'direct'
  },
  {
    label: 'Smartlink',
    value: 'trusted'
  }
]

export interface IAutoCpaSettingAttributes {
  id?: number | undefined
  creature_id?: number
  hourly_budget: number
  total_budget: number
  desired_margin: number
  representative_dataset: number
  historical_data_days: number
  recalculation_frequency: number
}

export type RecalculationFrequencyLabel = {
  id: number
  label: string
}

export const RecalculationFrequencyLabels: Array<RecalculationFrequencyLabel> =
  [
    {
      id: 3,
      label: '3 hours'
    },
    {
      id: 6,
      label: '6 hours'
    },
    {
      id: 12,
      label: '12 hours'
    },
    {
      id: 24,
      label: '24 hours'
    },
    {
      id: 48,
      label: '48 hours'
    }
  ]

export type CPMCalculationType = 'fixed' | 'smart' | 'auto_cpa' | 'auto_cpc'
export type CPMCalculationTypeOption = {
  label: string
  value: CPMCalculationType
}

export const CPMCalculationTypeOptions: Array<CPMCalculationTypeOption> = [
  {
    label: 'Fixed CPM',
    value: 'fixed'
  },
  {
    label: 'Smart CPM',
    value: 'smart'
  },
  {
    label: 'Auto CPA',
    value: 'auto_cpa'
  },
  {
    label: 'CPC',
    value: 'auto_cpc'
  }
]

export type VersionComparison = '<' | '<=' | '=' | '>' | '>='
export const VersionComparisonValues: VersionComparison[] = [
  '<',
  '<=',
  '=',
  '>',
  '>='
]

export type VersionComparisonOption = {
  value: VersionComparison
  label: string
}

export const VersionComparisonOptions: VersionComparisonOption[] = [
  {
    value: '<',
    label: 'Older than the version'
  },
  {
    value: '<=',
    label: 'This version or older'
  },
  {
    value: '=',
    label: 'This exact version'
  },
  {
    value: '>',
    label: 'Newer than the version'
  },
  {
    value: '>=',
    label: 'This version or newer'
  }
]

export interface SpecificVersion<T> {
  id: T
  version: string
  comparison: VersionComparison
}

// Type for relationship entry in API response, e.g.
// "data": [
//   {
//     ...
//     "relationships": {
//       "creatures": {
//         "data": [
//           {
//             "id": "12",
//             "type": "creature"
//           }, ...
//         ]
//       }
//     }
//   }
// ],
export type RelationshipEntry<T> = {
  id: string
  type: T
}
// Type for meta field in API, e.g.
// "meta": {
//   "total": 1
// }
export interface IResponseMeta {
  total: number
  sum?: string
}

export interface ISorter {
  field: string
  direction: string
}

// Provide/inject
export const CampaignValidationErrorsKey: InjectionKey<any> = Symbol(
  'Campaign form validation errors'
)
export const FeedFormatValidationErrorsKey: InjectionKey<any> = Symbol(
  'Integration format form validation errors'
)
export const FeedValidationErrorsKey: InjectionKey<any> = Symbol(
  'Feed form validation errors'
)
export const FeedFormatKey: InjectionKey<Ref<any | undefined>> =
  Symbol('Integration format')
export const PaypalAccountKey: InjectionKey<Ref<any | undefined>> =
  Symbol('Paypal account')
export const FeedKey: InjectionKey<Ref<any | undefined>> = Symbol('Feed')
export const CampaignKey: InjectionKey<Ref<any | undefined>> = Symbol(
  'Campaign for current creative'
)
export const MarketplaceKey: InjectionKey<Ref<any | undefined>> =
  Symbol('Marketplace')
export const FlatDealKey: InjectionKey<Ref<any | undefined>> =
  Symbol('Flat deal')
export const FlatDealValidationErrorsKey: InjectionKey<any> = Symbol(
  'Flat deal form validation errors'
)
export const CreativeKey: InjectionKey<Ref<any | undefined>> =
  Symbol('Current creative')

export const CreativeValidationErrorsKey: InjectionKey<any> = Symbol(
  'Creative form validation errors'
)
export const AutomationValidationErrorsKey: InjectionKey<any> = Symbol(
  'Automation form validation errors'
)
export const PaymentValidationErrorsKey: InjectionKey<any> = Symbol(
  'Payment form validation errors'
)
export const ProfileValidationErrorsKey: InjectionKey<any> = Symbol(
  'Profile form validation errors'
)
export const UnsubscribeValidationErrorsKey: InjectionKey<any> = Symbol(
  'Unsubscribe validation errors'
)
export const SpotsListValidationErrorsKey: InjectionKey<any> = Symbol(
  'SpotsList form validation errors'
)
export const CommonValidationErrorsKey: InjectionKey<any> = Symbol(
  'Form validation errors'
)
export const SpotValidationErrorsKey: InjectionKey<any> = Symbol(
  'Spot validation errors'
)

export enum ContactType {
  Corporate = 'corporate',
  Personal = 'personal'
}

export type ContactTypeLabel = {
  id: ContactType.Corporate | ContactType.Personal
  label: string
}

export const ContactTypeLabels: Array<ContactTypeLabel> = [
  {
    id: ContactType.Personal,
    label: 'Personal'
  },
  {
    id: ContactType.Corporate,
    label: 'Corporate'
  }
]

export enum TrafficType {
  Adult = 'Adult',
  Mainstream = 'Mainstream'
}

export enum TrafficQuality {
  Low = 1,
  Normal,
  High
}

export type TrafficQualityLabel = {
  id: TrafficQuality.Low | TrafficQuality.Normal | TrafficQuality.High
  label: string
}

export const TrafficQualityLabels: Array<TrafficQualityLabel> = [
  {
    id: TrafficQuality.Low,
    label: 'Low'
  },
  {
    id: TrafficQuality.Normal,
    label: 'Normal'
  },
  {
    id: TrafficQuality.High,
    label: 'High'
  }
]

export interface IMessage {
  text?: string
  id?: string | null
}

export enum GroupType {
  Date = 'date',
  Country = 'country',
  Creative = 'creature',
  Feed = 'feed',
  AdType = 'ad_type',
  SubId = 'pub_creature',
  Adblock = 'adblock',
  Browser = 'browser',
  Campaign = 'campaign',
  Connection = 'connection',
  Device = 'device',
  ImpType = 'imp_type',
  IP = 'ip',
  Platform = 'platform',
  ProxyType = 'proxy_type',
  RefererHost = 'referer_host',
  Spot = 'spot',
  Stream = 'stream',
  Topic = 'topic',
  Website = 'website',
  BannerSize = 'size',

  AdNetwork = 'adnetwork_squashed',
  AdnetworkCountry = 'adnetwork_and_country',
  AdNetworkType = 'adnetwork_type',
  RequestHost = 'request_host',
  Incognito = 'incognito',
  SubId1 = 'sub_id_1',
  SubId2 = 'sub_id_2',
  UserType = 'user_type',
  TrafficType = 'ranking_type',
  TrafficSource = 'traffic_source'
}

export type GroupTypeEntry = {
  key: GroupType
  label: string
  disabled: boolean
  group: string
}

export type GroupTypeLabel = {
  type_id: GroupType
  label: string
  aggregation: boolean
  group: string
  publisher: boolean
  advert: boolean
}

export const GroupTypeLabels: Array<GroupTypeLabel> = [
  {
    type_id: GroupType.Date,
    label: 'Date',
    aggregation: true,
    group: 'Most used',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Country,
    label: 'Country',
    aggregation: true,
    group: 'Most used',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Creative,
    label: 'Creative',
    aggregation: true,
    group: 'Most used',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Feed,
    label: 'Feed',
    aggregation: true,
    group: 'Most used',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.Campaign,
    label: 'Campaign',
    aggregation: true,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.Connection,
    label: 'Connection type',
    aggregation: false,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.ImpType,
    label: 'Imp type',
    aggregation: false,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.IP,
    label: 'IP address',
    aggregation: false,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.Spot,
    label: 'Spot',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.AdType,
    label: 'Ad Type',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.BannerSize,
    label: 'Banner size',
    aggregation: false,
    group: 'Other',
    publisher: true,
    advert: false
  },
  {
    type_id: GroupType.AdNetwork,
    label: 'Ad Network',
    aggregation: false,
    group: 'Other',
    publisher: true,
    advert: false
  },
  {
    type_id: GroupType.Website,
    label: 'Website',
    aggregation: false,
    group: 'Other',
    publisher: true,
    advert: false
  },
  {
    type_id: GroupType.Stream,
    label: 'Stream',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Topic,
    label: 'Topic',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: false
  },
  {
    type_id: GroupType.Device,
    label: 'Device',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Browser,
    label: 'Browser',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.Platform,
    label: 'Platform',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.ProxyType,
    label: 'Proxy type',
    aggregation: false,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.RefererHost,
    label: 'Referrer Host (Domain)',
    aggregation: false,
    group: 'Other',
    publisher: true,
    advert: true
  },
  {
    type_id: GroupType.SubId,
    label: 'SubID',
    aggregation: true,
    group: 'Other',
    publisher: false,
    advert: true
  },
  {
    type_id: GroupType.SubId1,
    label: 'SubID',
    aggregation: true,
    group: 'Other',
    publisher: true,
    advert: false
  }
]

export interface IGeneralResponse {
  meta: IResponseMeta
}

export interface IRequestPayload {
  limit?: number
  start?: number
  sorter?: ISorter
}

export interface ISpecificVersion {
  id: string
  version: string
  comparison: string
}

export interface IAttributes {
  id: string | number
}

export interface IItem {
  id: string
  attributes: IAttributes
}

export interface IAxe {
  title: string
  value: string
}

export enum MarketplaceStatus {
  Disabled = 'disabled',
  Requested = 'requested',
  Rejected = 'rejected',
  Enabled = 'enabled'
}

export enum OnboardingSteps {
  NeedFunds = 'need_funds',
  NeedCodes = 'need_codes',
  NeedCall = 'need_call',
  NeedSpots = 'need_spots',
  NeedCreatives = 'need_creatives',
  Onboarded = 'onboarded'
}

export enum TierRange {
  Tier1 = 1,
  Tier2,
  Tier3,
  None
}

export type TierLabel = {
  id: TierRange
  label: string
}

export type CountryEntry = {
  key: string
  label: string
  disabled: boolean
  tier: number
}

export interface IReportOption {
  name: string
  id: string
}

export const CPMModeOptions = [
  { value: '', label: 'Default (use publisher CPM mode)' },
  { value: 'cpm', label: 'CPM' },
  { value: 'diff_cpm', label: 'Diff CPM' },
  { value: 'diff_real_cpm', label: 'Diff Real CPM' },
  { value: 'real_cpm', label: 'Real CPM' },
  { value: 'diff_discrep_cpm', label: 'Diff Discrepancy CPM' }
] as const

export type CPMMode = (typeof CPMModeOptions)[number]['value']
